import React, { useState } from 'react';
import { useDeepCompareEffect, useEffectOnce } from 'react-use';
import { useDispatch } from 'react-redux';
import { useCurrentUser, useSearchSelector, useTranslate } from '@hooks';
import { searchOffers } from '@actions';
import { OfferApi } from '@api';
import { flatten, compact } from 'lodash';
import { getNameWithId } from '@dotone/react-ui-core/utils';
import { AsyncSelect } from '@dotone/react-ui-core';

const OfferSearchInput = ({
  name,
  formProps,
  active = false,
  searchParams = {},
  loadInitial = false,
  forImageCreative = false,
  forTextCreative = false,
  ...otherProps
}) => {
  const { t } = useTranslate('common');
  const dispatch = useDispatch();
  const [options, setOptions] = useState([]);
  const { locale } = useCurrentUser();

  const { getValues } = formProps;
  const ids = compact(flatten([getValues(name)]));
  const query = {
    locale,
    active,
    ...(forImageCreative ? { forImageCreative } : {}),
    ...(forTextCreative ? { forTextCreative } : {}),
    ...searchParams,
  };
  const { data: defaultValue, status } = useSearchSelector(({ offerSearch }) => offerSearch, {
    ids,
    ...query,
  });

  const isLimitReached = (item) =>
    (forImageCreative && item.imageCreativeReachedLimit) ||
    (forTextCreative && item.textCreativeReachedLimit);

  const formatData = (data) =>
    data.map((item) => ({
      value: item.id,
      label: isLimitReached(item)
        ? `${getNameWithId(item)} (${t('common:limitReached')})`
        : getNameWithId(item),
      isDisabled: isLimitReached(item),
    }));

  const handleLoad = (search) => OfferApi.search({ search, limit: 20, ...query });

  useDeepCompareEffect(() => {
    if (ids.length && !defaultValue.length) {
      dispatch(searchOffers({ ids, ...query }));
    }
  }, [dispatch, ids, defaultValue.length, query]);

  const loadInitialOptions = () => {
    handleLoad()
      .then((response) => {
        setOptions(formatData(response.data));
      })
      .catch(() => {});
  };

  useEffectOnce(() => {
    if (loadInitial && !ids.length) {
      loadInitialOptions();
    }
  });

  return (
    (status === 'success' || !ids.length) && (
      <AsyncSelect
        simple
        name={name}
        defaultValue={defaultValue}
        options={options}
        formatter={formatData}
        onOptionsChange={setOptions}
        onLoad={handleLoad}
        onClear={loadInitial ? loadInitialOptions : undefined}
        formProps={formProps}
        {...otherProps}
      />
    )
  );
};

export default React.memo(OfferSearchInput);
